import React from 'react';
import { UiAdminConfigsContext } from './UiAdminConfigsContext';
import { useGetUiAdminConfigEntry } from './useGetUiAdminConfigEntry';
import { useNotificationContext } from '../../global/hooks/useNotificationContext';
import { useUpsertUiAdminConfigEntry } from './useUpsertUiAdminConfigEntry';
import { useGetUiAdminConfigWritePermissions } from './useGetUiAdminConfigWritePermissions';
import { GEAR_UI_ADMIN_CONFIG_TYPE, GearUiAdminConfigType } from '../../Model/uiAdminConfig/GearUiAdminConfigType';
import { GearUiAdminConfigState } from '../../Model/uiAdminConfig/GearUiAdminConfigState';
import { useLocation } from 'react-router-dom';
import { GearHelpCenterItemState, GearHelpCenterState } from '../../Model/GearHelpCenterState';
import { useGlobalizationContext } from '../globalization/GlobalizationContext';
import { useTranslation } from '../globalization/useTranslation';

interface UiAdminConfigsProviderProps {
  children?: React.ReactNode;
}

export const UiAdminConfigsProvider: React.FC<UiAdminConfigsProviderProps> = ({ children }) => {
  const [configKey, setConfigKey] = React.useState<GearUiAdminConfigType>();
  const [configWritePermissionsKeys, setConfigWritePermissionsKeys] = React.useState<GearUiAdminConfigType[]>();
  const [uiAdminConfigState, setUiAdminConfigState] = React.useState<GearUiAdminConfigState>({});
  const [tempHelpCenterState, setTempHelpCenterState] = React.useState<Partial<GearHelpCenterState>>();
  const initializedKeys = React.useRef<Set<GearUiAdminConfigType>>(new Set());
  const {
    data: uiAdminConfigEntry,
    isLoading: isLoadingGetUiAdminConfigEntry,
    isFetched: isFetchedGetUiAdminConfigEntry
  } = useGetUiAdminConfigEntry(configKey);
  const { data: uiAdminConfigWritePermissions, isLoading: isLoadingUiAdminConfigWritePermissions } =
    useGetUiAdminConfigWritePermissions(configWritePermissionsKeys);
  const { mutate: mutateUpsertUiAdminConfig, isPending: isLoadingMutateUpsertUiConfigEntry } =
    useUpsertUiAdminConfigEntry();
  const { showToast } = useNotificationContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { pathname } = useLocation();

  const refreshUiAdminConfigState = (newConfigKey: GearUiAdminConfigType) => {
    setConfigKey(newConfigKey);
  };

  const handleUpsertUiAdminConfigEntry = (key: GearUiAdminConfigType, body: string): void => {
    if (key !== configKey) {
      setConfigKey(key);
    }
    mutateUpsertUiAdminConfig(
      { key, body },
      {
        onSuccess: () => {
          let messageKey = '';
          switch (key) {
            case GEAR_UI_ADMIN_CONFIG_TYPE.MESSAGE_BAR:
              messageKey = 'custom.notifications.messageBarSaved';
              break;
            case GEAR_UI_ADMIN_CONFIG_TYPE.MAINTENANCE_PAGE:
              messageKey = 'custom.notifications.maintenancePageSaved';
              break;
            case GEAR_UI_ADMIN_CONFIG_TYPE.HELP_CENTER:
              messageKey = 'custom.notifications.helpCenterSaved';
              break;
            case GEAR_UI_ADMIN_CONFIG_TYPE.REPORT_ISSUE:
              messageKey = 'custom.notifications.reportIssueSaved';
              break;
            default:
              break;
          }
          showToast({
            type: 'success',
            message: translate(messageKey)
          });

          initializedKeys.current.delete(key);
          refreshUiAdminConfigState(key);
        }
      }
    );
  };

  const [uiAdminConfigPreviewState, setUiAdminConfigPreviewState] = React.useState<GearUiAdminConfigState>({
    [GEAR_UI_ADMIN_CONFIG_TYPE.MESSAGE_BAR]: undefined,
    [GEAR_UI_ADMIN_CONFIG_TYPE.MAINTENANCE_PAGE]: undefined,
    [GEAR_UI_ADMIN_CONFIG_TYPE.HELP_CENTER]: undefined,
    [GEAR_UI_ADMIN_CONFIG_TYPE.REPORT_ISSUE]: undefined,
    [GEAR_UI_ADMIN_CONFIG_TYPE.ABOUT]: undefined
  });

  const resetPreviewState = React.useCallback(() => {
    setUiAdminConfigPreviewState({
      [GEAR_UI_ADMIN_CONFIG_TYPE.MESSAGE_BAR]: undefined,
      [GEAR_UI_ADMIN_CONFIG_TYPE.MAINTENANCE_PAGE]: undefined,
      [GEAR_UI_ADMIN_CONFIG_TYPE.HELP_CENTER]: undefined,
      [GEAR_UI_ADMIN_CONFIG_TYPE.REPORT_ISSUE]: undefined,
      [GEAR_UI_ADMIN_CONFIG_TYPE.ABOUT]: undefined
    });
  }, []);

  React.useEffect(() => {
    const keysToFetch = [
      GEAR_UI_ADMIN_CONFIG_TYPE.MAINTENANCE_PAGE,
      GEAR_UI_ADMIN_CONFIG_TYPE.MESSAGE_BAR,
      GEAR_UI_ADMIN_CONFIG_TYPE.HELP_CENTER,
      GEAR_UI_ADMIN_CONFIG_TYPE.REPORT_ISSUE,
      GEAR_UI_ADMIN_CONFIG_TYPE.ABOUT
    ];

    for (const key of keysToFetch) {
      if (!initializedKeys.current.has(key) && !(key in uiAdminConfigState)) {
        setConfigKey(key);
        return;
      }
    }
  }, [uiAdminConfigState]);

  React.useEffect(() => {
    if (!configKey || !setUiAdminConfigState || !isFetchedGetUiAdminConfigEntry) {
      return;
    }

    if (initializedKeys.current.has(configKey)) {
      return;
    }

    let parsedValue: any;
    if (uiAdminConfigEntry) {
      if (configKey === GEAR_UI_ADMIN_CONFIG_TYPE.ABOUT) {
        parsedValue = uiAdminConfigEntry;
      } else {
        try {
          parsedValue = JSON.parse(uiAdminConfigEntry);
        } catch {
          parsedValue = uiAdminConfigEntry;
        }
      }
    } else {
      parsedValue = {};
    }

    initializedKeys.current.add(configKey);

    setUiAdminConfigState(prevState => ({
      ...prevState,
      [configKey]: parsedValue
    }));
  }, [configKey, isFetchedGetUiAdminConfigEntry, uiAdminConfigEntry, uiAdminConfigState]);

  React.useEffect(() => {
    // reset preview State on route change
    setUiAdminConfigPreviewState({
      [GEAR_UI_ADMIN_CONFIG_TYPE.MESSAGE_BAR]: undefined,
      [GEAR_UI_ADMIN_CONFIG_TYPE.MAINTENANCE_PAGE]: undefined,
      [GEAR_UI_ADMIN_CONFIG_TYPE.HELP_CENTER]: undefined,
      [GEAR_UI_ADMIN_CONFIG_TYPE.REPORT_ISSUE]: undefined,
      [GEAR_UI_ADMIN_CONFIG_TYPE.ABOUT]: undefined
    });

    if (uiAdminConfigState.helpCenter) {
      const items: GearHelpCenterItemState[] = (uiAdminConfigState.helpCenter as GearHelpCenterState).items;
      setTempHelpCenterState({ items });
    } else {
      setTempHelpCenterState({});
    }
  }, [pathname, uiAdminConfigState.helpCenter]);

  React.useEffect(() => {
    if (
      isLoadingGetUiAdminConfigEntry ||
      isLoadingMutateUpsertUiConfigEntry ||
      isLoadingUiAdminConfigWritePermissions
    ) {
      return;
    }

    if (tempHelpCenterState !== undefined) {
      return;
    }

    if (uiAdminConfigState.helpCenter) {
      const items: GearHelpCenterItemState[] = (uiAdminConfigState.helpCenter as GearHelpCenterState).items;
      setTempHelpCenterState({ items });
    } else {
      setTempHelpCenterState({});
    }
  }, [
    isLoadingGetUiAdminConfigEntry,
    isLoadingMutateUpsertUiConfigEntry,
    isLoadingUiAdminConfigWritePermissions,
    tempHelpCenterState,
    uiAdminConfigState.helpCenter
  ]);

  return (
    <UiAdminConfigsContext.Provider
      value={{
        isLoading:
          isLoadingGetUiAdminConfigEntry ||
          isLoadingMutateUpsertUiConfigEntry ||
          isLoadingUiAdminConfigWritePermissions,
        upsertUiAdminConfigEntry: handleUpsertUiAdminConfigEntry,
        setConfigWritePermissionsKeys,
        uiAdminConfigWritePermissions,
        uiAdminConfigPreviewState,
        setUiAdminConfigPreviewState,
        resetPreviewState,
        uiAdminConfigState,
        refreshUiAdminConfigState,
        tempHelpCenterState,
        setTempHelpCenterState
      }}
    >
      {children}
    </UiAdminConfigsContext.Provider>
  );
};
